import React, { useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box } from '@mui/material';
import { FaArrowLeft, FaEye, FaEyeSlash } from 'react-icons/fa';
import {
  Container,
  Header,
  HeaderLeft,
  BackButton,
  Title,
  Content,
  ControlsContainer,
  HeaderButton
} from '../common/styles';
import { MainContainer } from './styles';
import { useAuth } from '../../contexts/AuthContext';
import { useAlert } from '../../contexts/AlertContext';
import ResultsCount from '../common/ResultsCount';
import MyAssignmentsGrid from './components/MyAssignmentsGrid';
import MyAssignmentsFilters from './components/MyAssignmentsFilters';
import useMyAssignmentsStore from '../../store/myAssignmentsStore';
import useGridNavigation from '../../hooks/useGridNavigation';

const MyAssignments = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showAlert } = useAlert();
  const gridRef = useRef(null);

  // Zustand store
  const {
    assignments,
    isLoading,
    isLoadingMore,
    pageInfo,
    employeeId,
    selectedBadges,
    selectedReviewTypes,
    selectedAboutTypes,
    showControls,
    fetchAssignmentsWithReset,
    fetchAssignmentsLoadMore,
    toggleBadge,
    toggleReviewType,
    toggleAboutType,
    setShowControls,
    setIsReturningFromDetails,
    isReturningFromDetails,
    getFilteredAssignments,
    setEmployeeId,
    resetFilters,
    lastViewedAssignmentId,
    setLastViewedAssignmentId,
    refreshAssignment,
    isRefreshingSingle,
  } = useMyAssignmentsStore();

  // Get filtered assignments once
  const filteredAssignments = getFilteredAssignments();

  // Grid navigation hook
  // IMPORTANT: Pass raw assignments for shouldLoadData checks (to avoid infinite loop when filters result in 0 items)
  // but we display filteredAssignments in the grid
  const {
    handleItemClick,
    handleGridScroll,
    showLoadingSpinner,
    shouldHideContent,
    loadMoreData,
    shouldLoadData,
  } = useGridNavigation({
    gridRef,
    items: assignments, // Use raw assignments for data loading checks
    columnCount: 3,
    rowHeight: 650,
    routes: {
      list: '/my-assignments',
      detail: (item) => `/skills-matrix/assignments/${item.id}/${employeeId}`,
      additionalRoutes: []
    },
    onReturnFromDetail: () => {
      setIsReturningFromDetails(true);
    },
    // Note: Not using onNavigateAway here because My Assignments navigates to
    // /skills-matrix/assignments/... which has a different path prefix, causing
    // the hook to incorrectly detect it as "navigating away". Instead, we manually
    // reset filters in handleBack when navigating to home.
    loadMoreDataCallback: fetchAssignmentsLoadMore,
    isLoading,
    isLoadingMore,
    pageInfo,
  });

  // Set employee ID when user is available
  useEffect(() => {
    if (user?.employee?.id && !employeeId) {
      setEmployeeId(user.employee.id);
    }
  }, [user?.employee?.id, employeeId, setEmployeeId]);

  // Fetch assignments on mount
  useEffect(() => {
    if (!user?.employee?.id) {
      return;
    }

    // If returning from detail, refresh the specific assignment that was viewed
    if (isReturningFromDetails && lastViewedAssignmentId) {
      refreshAssignment(lastViewedAssignmentId, user.employee.id);
      setIsReturningFromDetails(false);
      return;
    }

    // Initial load
    if (shouldLoadData() && !isReturningFromDetails) {
      fetchAssignmentsWithReset(user.employee.id);
    }
  }, [
    user?.employee?.id,
    shouldLoadData,
    isReturningFromDetails,
    lastViewedAssignmentId,
    fetchAssignmentsWithReset,
    setIsReturningFromDetails,
    refreshAssignment
  ]);

  // Helper to capture scroll state before navigation
  const captureScrollState = useCallback((assignment) => {
    const gridElement = gridRef.current?._outerRef;
    if (!gridElement) {
      return null;
    }

    const currentScrollPosition = gridElement.scrollTop;
    const items = getFilteredAssignments();
    const itemIndex = items.findIndex(item => item.id === assignment.id);
    const rowIndex = itemIndex !== -1 ? Math.floor(itemIndex / 3) : -1;
    const itemTopPosition = rowIndex * 650;
    const offsetFromTop = itemTopPosition - currentScrollPosition;

    return {
      scrollPosition: currentScrollPosition,
      itemId: assignment.id,
      itemIndex,
      rowIndex,
      offsetFromTop,
      timestamp: Date.now()
    };
  }, [getFilteredAssignments]);

  const handleBack = useCallback(() => {
    // Reset filters when navigating away from the section
    resetFilters();
    navigate('/');
  }, [navigate, resetFilters]);

  const hasFiltersApplied = selectedBadges.length > 0 || selectedReviewTypes.length > 0 || selectedAboutTypes.length > 0;

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <BackButton onClick={handleBack}>
            <FaArrowLeft /> Back to Home
          </BackButton>
          <Title>My Assignments</Title>
        </HeaderLeft>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <HeaderButton
            onClick={() => setShowControls(!showControls)}
            sx={{
              color: '#475569',
              borderColor: '#E2E8F0',
              '&:hover': {
                borderColor: '#CBD5E1',
                backgroundColor: '#F8FAFC',
              }
            }}
          >
            {showControls ? <FaEyeSlash /> : <FaEye />}
            {showControls ? 'Hide Filters' : 'Show Filters'}
          </HeaderButton>
        </Box>
      </Header>

      <Content>
        <ControlsContainer $show={showControls}>
          <MyAssignmentsFilters
            selectedBadges={selectedBadges}
            onBadgeToggle={toggleBadge}
            selectedReviewTypes={selectedReviewTypes}
            onReviewTypeToggle={toggleReviewType}
            selectedAboutTypes={selectedAboutTypes}
            onAboutTypeToggle={toggleAboutType}
          />
        </ControlsContainer>

        <MainContainer>
          <ResultsCount count={filteredAssignments.length} singular="assignment" plural="assignments" />

          <MyAssignmentsGrid
            assignments={filteredAssignments}
            isLoading={isLoading}
            isLoadingMore={isLoadingMore}
            pageInfo={{ ...pageInfo, totalCount: filteredAssignments.length }}
            handleItemClick={handleItemClick}
            handleGridScroll={handleGridScroll}
            showLoadingSpinner={showLoadingSpinner}
            shouldHideContent={shouldHideContent}
            loadMoreData={loadMoreData}
            gridRef={gridRef}
            employeeId={employeeId}
            hasFiltersApplied={hasFiltersApplied}
            captureScrollState={captureScrollState}
            setLastViewedAssignmentId={setLastViewedAssignmentId}
          />
        </MainContainer>
      </Content>
    </Container>
  );
};

export default MyAssignments;
